import { useEffect, useState, useCallback } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable, Badge } from "@/components/common/DataTable";
import { leadSourceService, tenantService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";

const TYPES = ["Web", "Referral", "Event", "Advertisement", "Cold Call", "Social", "Partner", "Other"];
const EMPTY = { name: "", description: "", type: "Other", tenantId: "" };

export default function LeadSourcesPage() {
  const { hasPermission, user } = useAuth();
  const isSuper = user?.isSuperAdmin;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await leadSourceService.list({ size: 200 }); setRows(d.items || []); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isSuper) tenantService.list().then((d) => setTenants(d.items || [])).catch(() => {}); }, [isSuper]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...EMPTY, ...r }); setOpen(true); };

  const save = async () => {
    if (!form.name) return toast.error("Name is required");
    if (isSuper && !editing && !form.tenantId) return toast.error("Select a tenant");
    setSaving(true);
    try {
      if (editing) { const { name, description, type } = form; await leadSourceService.update(editing.id, { name, description, type }); toast.success("Source updated"); }
      else { await leadSourceService.create(form); toast.success("Source created"); }
      setOpen(false); load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete source "${r.name}"?`)) return;
    try { await leadSourceService.remove(r.id); toast.success("Source deleted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const canCreate = hasPermission("LEAD_SOURCE.CREATE");
  const canEdit = hasPermission("LEAD_SOURCE.EDIT");
  const canDelete = hasPermission("LEAD_SOURCE.DELETE");

  const columns = [
    { key: "name", header: "Source", render: (r) => <span className="font-medium text-foreground">{r.name}</span> },
    { key: "type", header: "Type", render: (r) => <Badge color="blue">{r.type}</Badge> },
    { key: "description", header: "Description", render: (r) => <span className="text-muted-foreground">{r.description || "—"}</span> },
    { key: "leadCount", header: "Leads", render: (r) => <span className="text-muted-foreground">{r.leadCount ?? 0}</span> },
    {
      key: "_a", header: "",
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          {canEdit && <button onClick={() => openEdit(r)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground" data-testid={`source-edit-${r.id}`}><Pencil className="h-4 w-4" /></button>}
          {canDelete && <button onClick={() => remove(r)} className="p-1.5 rounded-md hover:bg-destructive/10 text-destructive" data-testid={`source-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>}
        </div>
      ),
    },
  ];

  return (
    <div data-testid="lead-sources-page">
      <PageHeader title="Lead Sources" subtitle="Define and track where your leads come from"
        actions={canCreate && (
          <button onClick={openCreate} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90 transition-colors" data-testid="source-create-btn">
            <Plus className="h-4 w-4" /> New Source
          </button>
        )}
      />
      <DataTable columns={columns} rows={rows} loading={loading} testid="lead-sources-table" empty="No lead sources yet" />

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-card text-foreground rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="source-dialog">
            <h3 className="text-lg font-bold mb-4">{editing ? "Edit" : "New"} Lead Source</h3>
            <div className="space-y-3.5">
              {isSuper && !editing && (
                <div>
                  <label className="block text-sm font-medium mb-1">Tenant *</label>
                  <select value={form.tenantId} onChange={(e) => setForm({ ...form, tenantId: e.target.value })} className="ciq-input" data-testid="source-tenant">
                    <option value="">Select tenant…</option>
                    {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName || t.name || t.id}</option>)}
                  </select>
                </div>
              )}
              <div><label className="block text-sm font-medium mb-1">Name *</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="ciq-input" data-testid="source-name" /></div>
              <div><label className="block text-sm font-medium mb-1">Type</label><select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="ciq-input" data-testid="source-type">{TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
              <div><label className="block text-sm font-medium mb-1">Description</label><input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="ciq-input" /></div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted">Cancel</button>
              <button onClick={save} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60" data-testid="source-save">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
